/**
 * JSON Schema definitions for validation
 * Used with AJV to reject malformed responses
 */

export const agentResponseSchema = {
  type: 'object',
  required: ['type'],
  properties: {
    type: {
      type: 'string',
      enum: ['text', 'action', 'error', 'tool_use']
    },
    content: {
      type: 'string'
    },
    action: {
      type: 'object',
      properties: {
        type: { type: 'string' },
        params: { type: 'object' }
      },
      required: ['type']
    },
    tool: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        input: { type: 'object' }
      },
      required: ['name']
    },
    error: {
      type: 'object',
      properties: {
        message: { type: 'string' },
        code: { type: 'string' }
      },
      required: ['message']
    }
  },
  additionalProperties: true
};

export const emailMessageSchema = {
  type: 'object',
  required: ['id'],
  properties: {
    id: { type: 'string', minLength: 1 },
    user_id: { type: ['string', 'null'] },
    userId: { type: ['string', 'null'] },
    account_id: { type: ['string', 'null'] },
    accountId: { type: ['string', 'null'] },
    thread_id: { type: ['string', 'null'] },
    threadId: { type: ['string', 'null'] },
    source: { 
      type: ['string', 'null'],
      enum: ['gmail', 'outlook', 'other', null]
    },
    provider_message_id: { type: ['string', 'null'] },
    providerMessageId: { type: ['string', 'null'] },
    provider_thread_id: { type: ['string', 'null'] },
    providerThreadId: { type: ['string', 'null'] },
    subject: { type: ['string', 'null'] },
    snippet: { type: ['string', 'null'] },
    sender_id: { type: ['string', 'null'] },
    senderId: { type: ['string', 'null'] },
    sender_email: { type: ['string', 'null'] },
    senderEmail: { type: ['string', 'null'] },
    sender_name: { type: ['string', 'null'] },
    senderName: { type: ['string', 'null'] },
    to_recipients: { type: ['string', 'null'] },
    toRecipients: { type: ['string', 'null'] },
    cc_recipients: { type: ['string', 'null'] },
    ccRecipients: { type: ['string', 'null'] },
    bcc_recipients: { type: ['string', 'null'] },
    bccRecipients: { type: ['string', 'null'] },
    body_text: { type: ['string', 'null'] },
    bodyText: { type: ['string', 'null'] },
    body_html: { type: ['string', 'null'] },
    bodyHtml: { type: ['string', 'null'] },
    body: { type: ['object', 'null'] },
    bodyPreview: { type: ['string', 'null'] },
    received_date: { type: ['string', 'null'] },
    receivedDate: { type: ['string', 'null'] },
    receivedDateTime: { type: ['string', 'null'] },
    sent_date: { type: ['string', 'null'] },
    sentDate: { type: ['string', 'null'] },
    internal_date: { type: ['string', 'null'] },
    internalDate: { type: ['string', 'null'] },
    is_read: { type: ['string', 'boolean', 'number', 'null'] },
    isRead: { type: ['string', 'boolean', 'number', 'null'] },
    is_starred: { type: ['string', 'boolean', 'number', 'null'] },
    isStarred: { type: ['string', 'boolean', 'number', 'null'] },
    is_important: { type: ['string', 'boolean', 'number', 'null'] },
    isImportant: { type: ['string', 'boolean', 'number', 'null'] },
    importance: { type: ['string', 'null'] },
    is_draft: { type: ['string', 'boolean', 'number', 'null'] },
    isDraft: { type: ['string', 'boolean', 'number', 'null'] },
    is_sent: { type: ['string', 'boolean', 'number', 'null'] },
    isSent: { type: ['string', 'boolean', 'number', 'null'] },
    is_trash: { type: ['string', 'boolean', 'number', 'null'] },
    isTrash: { type: ['string', 'boolean', 'number', 'null'] },
    is_spam: { type: ['string', 'boolean', 'number', 'null'] },
    isSpam: { type: ['string', 'boolean', 'number', 'null'] },
    labels: { type: ['string', 'array', 'null'] },
    has_attachments: { type: ['string', 'boolean', 'number', 'null'] },
    hasAttachments: { type: ['string', 'boolean', 'number', 'null'] },
    attachment_count: { type: ['number', 'null'], minimum: 0 },
    attachmentCount: { type: ['number', 'null'], minimum: 0 },
    ai_summary: { type: ['string', 'null'] },
    aiSummary: { type: ['string', 'null'] },
    ai_sentiment: { type: ['string', 'null'] },
    aiSentiment: { type: ['string', 'null'] },
    ai_priority: { type: ['string', 'null'] },
    aiPriority: { type: ['string', 'null'] },
    provider_data: { type: ['string', 'object', 'null'] },
    providerData: { type: ['string', 'object', 'null'] },
    parentFolderId: { type: ['string', 'null'] },
    size_estimate: { type: ['number', 'null'], minimum: 0 },
    sizeEstimate: { type: ['number', 'null'], minimum: 0 },
    created_at: { type: ['string', 'null'] },
    createdAt: { type: ['string', 'null'] },
    updated_at: { type: ['string', 'null'] },
    updatedAt: { type: ['string', 'null'] },
    from: { type: ['object', 'null'] },
    '@odata.etag': { type: ['string', 'null'] }
  },
  additionalProperties: true
};

export const calendarEventSchema = {
  type: 'object',
  required: ['id', 'user_id', 'title', 'start_time'],
  properties: {
    id: { type: 'string', minLength: 1 },
    user_id: { type: 'string', minLength: 1 },
    account_id: { type: 'string' },
    series_id: { type: ['string', 'null'] },
    source: { 
      type: 'string',
      enum: ['gmail', 'outlook', 'local']
    },
    provider_event_id: { type: 'string' },
    calendar_id: { type: ['string', 'null'] },
    title: { type: 'string', minLength: 1 },
    description: { type: ['string', 'null'] },
    location: { type: ['string', 'null'] },
    location_details: { type: ['string', 'null'] },
    start_time: { type: 'string' },
    end_time: { type: ['string', 'null'] },
    start_date: { type: ['string', 'null'] },
    end_date: { type: ['string', 'null'] },
    timezone: { type: ['string', 'null'] },
    is_all_day: { type: ['string', 'boolean', 'number'] },
    is_recurring: { type: ['string', 'boolean', 'number'] },
    recurrence_rule: { type: ['string', 'null'] },
    recurrence_exception: { type: ['string', 'null'] },
    original_start_time: { type: ['string', 'null'] },
    organizer_id: { type: ['string', 'null'] },
    organizer_email: { type: ['string', 'null'] },
    organizer_name: { type: ['string', 'null'] },
    attendees: { type: ['string', 'null'] },
    attendee_count: { type: 'number', minimum: 0 },
    status: { 
      type: 'string',
      enum: ['confirmed', 'tentative', 'cancelled']
    },
    visibility: { type: ['string', 'null'] },
    response_status: { type: ['string', 'null'] },
    reminders: { type: ['string', 'null'] },
    conference_data: { type: ['string', 'null'] },
    attachments: { type: ['string', 'null'] },
    ai_summary: { type: ['string', 'null'] },
    ai_agenda: { type: ['string', 'null'] },
    ai_priority: { type: ['string', 'null'] },
    provider_data: { type: ['string', 'null'] },
    category: { type: ['string', 'null'] },
    event_type: { type: 'string' },
    created_at: { type: 'string' },
    updated_at: { type: 'string' }
  },
  additionalProperties: false
};

export const oauthTokenResponseSchema = {
  type: 'object',
  required: ['access_token', 'token_type'],
  properties: {
    access_token: { type: 'string', minLength: 1 },
    refresh_token: { type: ['string', 'null'] },
    token_type: { type: 'string' },
    expires_in: { type: 'number', minimum: 0 },
    scope: { type: 'string' },
    id_token: { type: ['string', 'null'] }
  },
  additionalProperties: true
};

export const emailActionParamsSchema = {
  type: 'object',
  properties: {
    to: {
      oneOf: [
        { type: 'string', minLength: 1 },
        { type: 'array', items: { type: 'string', minLength: 1 } }
      ]
    },
    subject: { type: 'string', minLength: 1 },
    body: { type: 'string' },
    messageId: { type: 'string' },
    folder: { type: 'string' },
    query: { type: 'string' }
  },
  additionalProperties: true
};

export const userPreferencesSchema = {
  type: 'object',
  properties: {
    theme: { 
      type: 'string',
      enum: ['light', 'dark', 'auto']
    },
    emails_per_page: { 
      type: 'number',
      minimum: 10,
      maximum: 100
    },
    events_per_page: { 
      type: 'number',
      minimum: 10,
      maximum: 100
    },
    default_view: {
      type: 'string',
      enum: ['inbox', 'sent', 'drafts', 'calendar', 'tasks']
    },
    email_notifications: { type: ['string', 'boolean', 'number'] },
    calendar_reminders: { type: ['string', 'boolean', 'number'] },
    reminder_minutes: { 
      type: 'number',
      minimum: 0,
      maximum: 1440
    },
    auto_sync: { type: ['string', 'boolean', 'number'] },
    sync_interval: { 
      type: 'number',
      minimum: 5,
      maximum: 60
    }
  },
  additionalProperties: true
};

/**
 * Workflow Node Schemas
 * Used to validate workflow node configurations before execution
 */

export const workflowNodeBaseSchema = {
  type: 'object',
  required: ['id', 'type', 'title', 'description', 'config'],
  properties: {
    id: { type: 'string', minLength: 1 },
    type: {
      type: 'string',
      enum: ['trigger', 'condition', 'action', 'ai', 'loop', 'branch']
    },
    title: { type: 'string', minLength: 1 },
    description: { type: 'string' },
    config: { type: 'object' }
  },
  additionalProperties: false
};

export const sendEmailActionSchema = {
  type: 'object',
  // Note: 'to' and 'subject' are not required here because they can be inferred from:
  // 1. Previous AI nodes (ai_generate_reply)
  // 2. Trigger context (trigger.from, trigger.subject)
  // 3. Template variables that will be interpolated at runtime
  // Validation happens at runtime in sendEmail() method after interpolation
  required: [],
  properties: {
    to: {
      oneOf: [
        { type: 'string', minLength: 1 },
        { type: 'array', items: { type: 'string', minLength: 1 }, minItems: 1 }
      ]
    },
    subject: { type: 'string', minLength: 1 },
    body: { type: 'string' },
    message: { type: 'string' },
    content: { type: 'string' },
    text: { type: 'string' },
    recipient: { type: 'string' },
    emailSubject: { type: 'string' },
    title: { type: 'string' },
    useHtml: { type: 'boolean' },
    inReplyTo: { type: 'string' },
    defaultBody: { type: 'string' },
    subject_prefix: { type: 'string' },
    auto_reply: { type: 'boolean' }
  },
  additionalProperties: true
};

export const extractContactInfoActionSchema = {
  type: 'object',
  properties: {
    subject: { type: 'string' },
    body: { type: 'string' },
    from: { type: 'string' },
    to: { type: 'string' },
    cc: { type: 'string' }
  },
  additionalProperties: true
};

export const createTaskActionSchema = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    description: { type: 'string' },
    dueDate: { type: 'string' },
    priority: {
      type: 'string',
      enum: ['high', 'medium', 'low']
    },
    tags: { type: ['string', 'null'] }
  },
  additionalProperties: true
};

export const updateContactIntelligenceActionSchema = {
  type: 'object',
  properties: {
    contactEmail: { type: 'string' },
    analysis: { type: 'object' }
  },
  additionalProperties: true
};

export const applyLabelsActionSchema = {
  type: 'object',
  properties: {
    emailId: { type: 'string' },
    labels: {
      oneOf: [
        { type: 'string' },
        { type: 'array', items: { type: 'string' } }
      ]
    }
  },
  additionalProperties: true
};

export const analyzeRelationshipActionSchema = {
  type: 'object',
  properties: {
    subject: { type: 'string' },
    body: { type: 'string' },
    from: { type: 'string' },
    include_sentiment: { type: 'boolean' }
  },
  additionalProperties: true
};

export const searchRelatedEmailsActionSchema = {
  type: 'object',
  properties: {
    query: { type: 'string' },
    searchQuery: { type: 'string' },
    subject: { type: 'string' },
    from: { type: 'string' },
    limit: { type: 'number', minimum: 1, maximum: 100 }
  },
  additionalProperties: true
};

export const generateBriefingActionSchema = {
  type: 'object',
  properties: {
    maxTokens: { type: 'number', minimum: 100, maximum: 4000 }
  },
  additionalProperties: true
};

export const aiNodeSchema = {
  type: 'object',
  required: ['prompt'],
  properties: {
    prompt: { type: 'string', minLength: 1 },
    model: { 
      type: 'string',
      enum: ['gpt-5', 'gpt-4o', 'claude-sonnet-4', 'auto']
    },
    maxTokens: { type: 'number', minimum: 1, maximum: 4000 },
    search: { type: 'boolean' }
  },
  additionalProperties: true
};

/**
 * AI Output Schemas - Strict structure requirements for AI-generated responses
 */

export const aiClassificationOutputSchema = {
  type: 'object',
  required: ['category', 'priority', 'confidence'],
  properties: {
    category: {
      type: 'string',
      enum: ['work', 'personal', 'newsletter', 'promotional', 'social', 'spam', 'other']
    },
    priority: {
      type: 'string',
      enum: ['high', 'medium', 'low']
    },
    isUrgent: { type: 'boolean' },
    suggestedTags: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 0,
      maxItems: 10
    },
    confidence: {
      type: 'number',
      minimum: 0,
      maximum: 100
    },
    sentiment: {
      type: 'string',
      enum: ['positive', 'neutral', 'negative', 'urgent', 'mixed']
    },
    requiresResponse: { type: 'boolean' },
    summary: { type: 'string' }
  },
  additionalProperties: false
};

export const aiExtractionOutputSchema = {
  type: 'object',
  required: ['entities'],
  properties: {
    entities: {
      type: 'array',
      items: {
        type: 'object',
        required: ['type', 'value'],
        properties: {
          type: {
            type: 'string',
            enum: ['person', 'company', 'date', 'location', 'action_item', 'email', 'phone', 'url']
          },
          value: { type: 'string', minLength: 1 },
          confidence: { type: 'number', minimum: 0, maximum: 100 }
        },
        additionalProperties: false
      }
    },
    actionItems: {
      type: 'array',
      items: {
        type: 'object',
        required: ['task'],
        properties: {
          task: { type: 'string', minLength: 1 },
          dueDate: { type: ['string', 'null'] },
          priority: {
            type: 'string',
            enum: ['high', 'medium', 'low']
          }
        },
        additionalProperties: false
      }
    }
  },
  additionalProperties: false
};

export const aiGenerationOutputSchema = {
  type: 'object',
  required: ['generatedText'],
  properties: {
    generatedText: { type: 'string', minLength: 1 },
    tokenCount: { type: 'number', minimum: 1 },
    model: { type: 'string' }
  },
  additionalProperties: false
};

export const aiResearchOutputSchema = {
  type: 'object',
  required: ['research'],
  properties: {
    research: { type: 'string', minLength: 1 },
    sources: {
      type: 'array',
      items: {
        type: 'object',
        required: ['url', 'title'],
        properties: {
          url: { type: 'string', minLength: 1 },
          title: { type: 'string', minLength: 1 },
          snippet: { type: 'string' }
        },
        additionalProperties: false
      }
    },
    keyFindings: {
      type: 'array',
      items: { type: 'string', minLength: 1 }
    }
  },
  additionalProperties: false
};

export const aiAnalysisOutputSchema = {
  type: 'object',
  required: ['analysis'],
  properties: {
    analysis: { type: 'string', minLength: 1 },
    insights: {
      type: 'array',
      items: {
        type: 'object',
        required: ['insight', 'importance'],
        properties: {
          insight: { type: 'string', minLength: 1 },
          importance: {
            type: 'string',
            enum: ['high', 'medium', 'low']
          },
          category: { type: 'string' }
        },
        additionalProperties: false
      }
    },
    recommendations: {
      type: 'array',
      items: { type: 'string', minLength: 1 }
    },
    confidence: {
      type: 'number',
      minimum: 0,
      maximum: 100
    }
  },
  additionalProperties: false
};

export const relationshipAnalysisOutputSchema = {
  type: 'object',
  required: ['relationshipStage', 'engagementLevel', 'sentiment'],
  properties: {
    relationshipStage: {
      type: 'string',
      enum: ['new', 'developing', 'established', 'strong', 'at_risk']
    },
    engagementLevel: {
      type: 'string',
      enum: ['low', 'medium', 'high']
    },
    trustLevel: {
      type: 'string',
      enum: ['building', 'moderate', 'high']
    },
    communicationStyle: { type: 'string', minLength: 1 },
    sentiment: {
      type: 'string',
      enum: ['positive', 'neutral', 'negative', 'mixed']
    },
    powerDynamic: {
      type: 'string',
      enum: ['peer', 'authority', 'subordinate', 'equal']
    },
    insights: {
      type: 'array',
      items: { type: 'string', minLength: 1 }
    },
    recommendations: {
      type: 'array',
      items: { type: 'string', minLength: 1 }
    },
    relationshipHealth: {
      type: 'string',
      enum: ['excellent', 'good', 'fair', 'needs_attention']
    }
  },
  additionalProperties: false
};

export const contactExtractionOutputSchema = {
  type: 'object',
  required: ['contacts'],
  properties: {
    contacts: {
      type: 'array',
      items: {
        type: 'object',
        required: ['email'],
        properties: {
          email: { type: 'string', minLength: 3 },
          name: { type: 'string' },
          company: { type: 'string' },
          jobTitle: { type: 'string' },
          phone: { type: 'string' },
          source: { type: 'string' }
        },
        additionalProperties: false
      }
    },
    primaryContact: {
      type: 'object',
      required: ['email'],
      properties: {
        email: { type: 'string', minLength: 3 },
        name: { type: 'string' },
        relationship: { type: 'string' }
      },
      additionalProperties: false
    }
  },
  additionalProperties: false
};

export const conditionNodeSchema = {
  type: 'object',
  required: ['condition'],
  properties: {
    condition: {
      type: 'object',
      required: ['field', 'operator'],
      properties: {
        field: { type: 'string', minLength: 1 },
        operator: {
          type: 'string',
          enum: ['equals', 'not_equals', 'contains', 'greater_than', 'less_than', 'exists', 'is_empty']
        },
        value: { type: ['string', 'number', 'boolean', 'null'] }
      }
    },
    continueOnError: { type: 'boolean' }
  },
  additionalProperties: true
};

// Map action titles to their schemas
export const ACTION_SCHEMAS: Record<string, any> = {
  'Send Email': sendEmailActionSchema,
  'send_email': sendEmailActionSchema,
  'Send Reply': sendEmailActionSchema,
  'Send Confirmation': sendEmailActionSchema,
  'extract_contact_info': extractContactInfoActionSchema,
  'create_task': createTaskActionSchema,
  'update_contact_intelligence': updateContactIntelligenceActionSchema,
  'apply_labels': applyLabelsActionSchema,
  'analyze_relationship': analyzeRelationshipActionSchema,
  'search_related_emails': searchRelatedEmailsActionSchema,
  'Search Related Emails': searchRelatedEmailsActionSchema,
  'generate_briefing': generateBriefingActionSchema
};
